import {
  ACK_COMPLETE_COLOR,
  ACK_NOT_STARTED_COLOR,
  ACK_PARTIAL_COLOR,
  ACK_STATUS_LEGEND,
  NA_COLOR,
  TOTAL_SIGNATORIES,
} from '../omnidashboard.config';
import {
  AcknowledgementMatrixStatus,
  AcknowledgementStatusEntry,
  MatrixCellData,
  MetricData,
  MetricSummaryBucket,
  OmniMetricDefinition,
} from '../omnidashboard.models';

const DEFAULT_NA_METRIC: MetricData = {
  displayValue: 'N/A',
  color: NA_COLOR,
  value: null,
  signedCount: 0,
};

const ACKNOWLEDGEMENT_STATUS_BUCKETS: MetricSummaryBucket[] = [
  { value: 'Complete',         label: 'Complete',    color: ACK_COMPLETE_COLOR,    iconClass: 'fa-check',     tooltip: 'All 3 signatories (Exec Sponsor, Chair, Co-Chair) have signed' },
  { value: 'Partially Signed', label: 'In Progress', color: ACK_PARTIAL_COLOR,     iconClass: 'fa-adjust',    tooltip: '1 or 2 of 3 signatories have signed' },
  { value: 'Not Started',      label: 'Not Started', color: ACK_NOT_STARTED_COLOR, iconClass: 'fa-circle-o',  tooltip: 'No signatories have signed yet' },
];

const ACK_STATUS_ICON = new Map<string, string>(
  ACKNOWLEDGEMENT_STATUS_BUCKETS.map(b => [b.value, b.iconClass])
);

export function mapToAckStatus(item: AcknowledgementStatusEntry): AcknowledgementMatrixStatus {
  // Complete when all three roles (exec sponsor, chair, co-chair) have signed.
  const signedCount = [item.executiveSponsorSigned, item.chairSigned, item.coChairSigned]
    .filter((signature) => !!signature)
    .length;

  if (signedCount === TOTAL_SIGNATORIES) {
    return {
      statusLabel: 'Complete',
      displayValue: ACK_STATUS_ICON.get('Complete')!,
      color: ACK_COMPLETE_COLOR,
      signedCount,
    };
  }
  if (signedCount > 0) {
    return {
      statusLabel: 'Partially Signed',
      displayValue: ACK_STATUS_ICON.get('Partially Signed')!,
      color: ACK_PARTIAL_COLOR,
      signedCount,
    };
  }
  return {
    statusLabel: 'Not Started',
    displayValue: ACK_STATUS_ICON.get('Not Started')!,
    color: ACK_NOT_STARTED_COLOR,
    signedCount,
  };
}

export function buildAckStatusMap(items: AcknowledgementStatusEntry[]): Map<number, AcknowledgementMatrixStatus> {
  const statusMap = new Map<number, AcknowledgementMatrixStatus>();
  items.forEach((item) => {
    statusMap.set(item.chapterId, mapToAckStatus(item));
  });
  return statusMap;
}

export function resolveAckMetric(
  ackStatusMap: Map<number, AcknowledgementMatrixStatus>,
  cell: MatrixCellData
): MetricData {
  // Chapters missing from the API response are treated as not started (still has a chapter).
  const ackStatus = ackStatusMap.get(cell.chapterId) || {
    statusLabel: 'Not Started' as 'Not Started',
    displayValue: ACK_STATUS_ICON.get('Not Started')!,
    color: ACK_NOT_STARTED_COLOR,
    signedCount: 0,
  };
  return {
    displayValue: ackStatus.displayValue,
    color: ackStatus.color,
    value: ackStatus.statusLabel,
    signedCount: ackStatus.signedCount,
  };
}

export const ACKNOWLEDGEMENT_STATUS_METRIC: OmniMetricDefinition = {
  key: 'acknowledgement-status',
  label: 'Acknowledgement Status',
  legend: ACK_STATUS_LEGEND,
  summaryBuckets: ACKNOWLEDGEMENT_STATUS_BUCKETS,
  naMetric: DEFAULT_NA_METRIC,
  tooltipFormatter: (cell: MatrixCellData, metric: MetricData): string => {
    const name = cell.siteName + ' – ' + cell.bpgName;
    if (!metric.value) {
      return name;
    }
    const suffix = metric.signedCount != null ? ' (' + metric.signedCount + '/' + TOTAL_SIGNATORIES + ')' : '';
    return name + ' · ' + metric.value + suffix;
  },
};
